import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import ReactDOM from "react-dom";
import DOMPurify from "dompurify";
import SearchableSelect from "../components/SearchableSelect";
import { surveys as surveysApi, hotels as hotelsApi } from "../api/endpoints";
import type { Survey, SurveyCategory, SurveyQuestion } from "../types";

/* ------------------------------------------------------------------ */
/*  Local types                                                       */
/* ------------------------------------------------------------------ */

interface SurveyListItem {
  id: number;
  name: string;
  is_active: boolean;
}

interface PointValue {
  id: number;
  name: string;
  value: number | null;
  used_in_surveys?: string[];
}

interface QuestionChoice {
  id: number;
  point_value_id: number;
  point_value_name: string;
  point_value_value: number | null;
}

interface QuestionWithChoices extends SurveyQuestion {
  choices: QuestionChoice[];
}

interface CategoryWithChoices extends Omit<SurveyCategory, "questions"> {
  questions: QuestionWithChoices[];
}

/* ------------------------------------------------------------------ */
/*  Toast                                                             */
/* ------------------------------------------------------------------ */

function Toast({
  message,
  type,
  onClose,
}: {
  message: string;
  type: "success" | "error";
  onClose: () => void;
}) {
  useEffect(() => {
    const timer = setTimeout(onClose, 4000);
    return () => clearTimeout(timer);
  }, [onClose]);

  return (
    <div
      className={`fixed bottom-6 right-6 z-[60] flex items-center gap-3 rounded-xl px-5 py-3 shadow-lg text-sm font-medium rise-in ${
        type === "success"
          ? "bg-emerald-600 text-white"
          : "bg-red-600 text-white"
      }`}
    >
      {message}
      <button onClick={onClose} className="ml-2 opacity-70 hover:opacity-100">
        &times;
      </button>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Inline Editable Text                                              */
/* ------------------------------------------------------------------ */

function InlineEdit({
  value,
  onSave,
  className = "",
  tag: Tag = "span",
  placeholder = "Click to edit",
  multiline = false,
}: {
  value: string;
  onSave: (val: string) => void;
  className?: string;
  tag?: "span" | "h2" | "h3" | "p";
  placeholder?: string;
  multiline?: boolean;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement>(null);

  useEffect(() => {
    setDraft(value);
  }, [value]);

  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [editing]);

  const commit = () => {
    setEditing(false);
    const trimmed = draft.trim();
    if (trimmed && trimmed !== value) {
      onSave(trimmed);
    } else {
      setDraft(value);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !multiline) {
      e.preventDefault();
      commit();
    }
    if (e.key === "Escape") {
      setDraft(value);
      setEditing(false);
    }
  };

  if (editing) {
    if (multiline) {
      return (
        <textarea
          ref={inputRef as React.RefObject<HTMLTextAreaElement>}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={commit}
          onKeyDown={handleKeyDown}
          className={`textarea-shell w-full ${className}`}
          rows={3}
        />
      );
    }
    return (
      <input
        ref={inputRef as React.RefObject<HTMLInputElement>}
        type="text"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={handleKeyDown}
        className={`input-shell w-full ${className}`}
      />
    );
  }

  return (
    <Tag
      className={`cursor-pointer hover:bg-slate-100 rounded px-1 -mx-1 transition-colors ${className} ${
        !value ? "text-slate-400 italic" : ""
      }`}
      onClick={() => setEditing(true)}
      title="Click to edit"
    >
      {value || placeholder}
    </Tag>
  );
}

/* ------------------------------------------------------------------ */
/*  Chevron icon                                                      */
/* ------------------------------------------------------------------ */

function ChevronIcon({ open, className = "" }: { open: boolean; className?: string }) {
  return (
    <svg
      className={`h-4 w-4 text-slate-400 transition-transform ${open ? "rotate-180" : ""} ${className}`}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2}
    >
      <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
    </svg>
  );
}

/* ------------------------------------------------------------------ */
/*  Arrow icons for ordering                                          */
/* ------------------------------------------------------------------ */

function ArrowUpIcon() {
  return (
    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M5 15l7-7 7 7" />
    </svg>
  );
}

function ArrowDownIcon() {
  return (
    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
    </svg>
  );
}

/* ------------------------------------------------------------------ */
/*  Helper: extract error message                                     */
/* ------------------------------------------------------------------ */

function extractError(err: unknown): string {
  if (err && typeof err === "object" && "response" in err) {
    const resp = (err as any).response;
    if (resp?.data?.detail) {
      const d = resp.data.detail;
      return typeof d === "string" ? d : JSON.stringify(d);
    }
  }
  if (err instanceof Error) return err.message;
  return "An unexpected error occurred";
}

/* ------------------------------------------------------------------ */
/*  Point Values Manager (bottom collapsible panel)                   */
/* ------------------------------------------------------------------ */

function PointValuesManager({
  pointValues,
  onRefresh,
  showToast,
}: {
  pointValues: PointValue[];
  onRefresh: () => void;
  showToast: (msg: string, type: "success" | "error") => void;
}) {
  const [open, setOpen] = useState(false);
  const [newName, setNewName] = useState("");
  const [newValue, setNewValue] = useState("");
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editName, setEditName] = useState("");
  const [editValue, setEditValue] = useState("");
  const [saving, setSaving] = useState(false);

  const handleCreate = async () => {
    if (!newName.trim()) return;
    setSaving(true);
    try {
      await surveysApi.createPointValue({
        name: newName.trim(),
        value: newValue !== "" ? Number(newValue) : undefined,
      });
      setNewName("");
      setNewValue("");
      onRefresh();
      showToast("Point value created", "success");
    } catch (err) {
      showToast(extractError(err), "error");
    } finally {
      setSaving(false);
    }
  };

  const handleUpdate = async (id: number) => {
    if (!editName.trim()) return;
    setSaving(true);
    try {
      await surveysApi.updatePointValue(id, {
        name: editName.trim(),
        value: editValue !== "" ? Number(editValue) : undefined,
      });
      setEditingId(null);
      onRefresh();
      showToast("Point value updated", "success");
    } catch (err) {
      showToast(extractError(err), "error");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: number, name: string) => {
    if (!window.confirm(`Delete point value "${name}"? This cannot be undone.`)) return;
    try {
      await surveysApi.deletePointValue(id);
      onRefresh();
      showToast("Point value deleted", "success");
    } catch (err) {
      showToast(extractError(err), "error");
    }
  };

  const startEdit = (pv: PointValue) => {
    setEditingId(pv.id);
    setEditName(pv.name);
    setEditValue(pv.value != null ? String(pv.value) : "");
  };

  return (
    <div className="surface-panel border border-slate-200 rounded-xl overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="flex w-full items-center justify-between px-5 py-3 bg-slate-50 hover:bg-slate-100 transition-colors"
      >
        <span className="label-caps flex items-center gap-2">
          Point Values
          <span className="badge-pill text-xs">{pointValues.length}</span>
        </span>
        <ChevronIcon open={open} />
      </button>

      {open && (
        <div className="p-5 rise-in">
          <p className="text-xs text-slate-500 mb-4">
            Global point values used as answer choices across all surveys. Changes here affect all questions referencing these values.
          </p>

          <table className="table-modern w-full mb-4">
            <thead>
              <tr>
                <th className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider pb-2">Name</th>
                <th className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider pb-2">Value</th>
                <th className="text-right text-xs font-semibold text-slate-500 uppercase tracking-wider pb-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {pointValues.map((pv) => (
                <tr key={pv.id} className="border-t border-slate-100">
                  {editingId === pv.id ? (
                    <>
                      <td className="py-2 pr-2">
                        <input
                          type="text"
                          className="input-shell w-full text-sm"
                          value={editName}
                          onChange={(e) => setEditName(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") handleUpdate(pv.id);
                            if (e.key === "Escape") setEditingId(null);
                          }}
                          autoFocus
                        />
                      </td>
                      <td className="py-2 pr-2">
                        <input
                          type="number"
                          className="input-shell w-24 text-sm"
                          value={editValue}
                          onChange={(e) => setEditValue(e.target.value)}
                          placeholder="null"
                          onKeyDown={(e) => {
                            if (e.key === "Enter") handleUpdate(pv.id);
                            if (e.key === "Escape") setEditingId(null);
                          }}
                        />
                      </td>
                      <td className="py-2 text-right space-x-2">
                        <button
                          className="button-primary text-xs px-3 py-1"
                          onClick={() => handleUpdate(pv.id)}
                          disabled={saving}
                        >
                          Save
                        </button>
                        <button
                          className="button-ghost text-xs px-3 py-1"
                          onClick={() => setEditingId(null)}
                        >
                          Cancel
                        </button>
                      </td>
                    </>
                  ) : (
                    <>
                      <td className="py-2 text-sm font-medium">{pv.name}</td>
                      <td className="py-2 text-sm">
                        {pv.value != null ? (
                          <span
                            className={`font-mono text-xs px-2 py-0.5 rounded ${
                              pv.value > 0
                                ? "bg-emerald-50 text-emerald-700"
                                : pv.value < 0
                                ? "bg-red-50 text-red-700"
                                : "bg-slate-100 text-slate-600"
                            }`}
                          >
                            {pv.value > 0 ? "+" : ""}
                            {pv.value}
                          </span>
                        ) : (
                          <span className="text-slate-400 text-xs italic">null</span>
                        )}
                      </td>
                      <td className="py-2 text-right space-x-2">
                        <button
                          className="button-ghost text-xs px-3 py-1"
                          onClick={() => startEdit(pv)}
                        >
                          Edit
                        </button>
                        <button
                          className="button-danger text-xs px-3 py-1"
                          onClick={() => handleDelete(pv.id, pv.name)}
                        >
                          Delete
                        </button>
                      </td>
                    </>
                  )}
                </tr>
              ))}
              {pointValues.length === 0 && (
                <tr>
                  <td colSpan={3} className="py-4 text-center text-sm text-slate-400">
                    No point values defined yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>

          {/* Create new point value */}
          <div className="flex items-end gap-3 pt-2 border-t border-slate-100">
            <div className="flex-1">
              <label className="eyebrow mb-1 block">Name</label>
              <input
                type="text"
                className="input-shell w-full text-sm"
                placeholder='e.g. "Yes", "No", "N/A"'
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") handleCreate();
                }}
              />
            </div>
            <div className="w-28">
              <label className="eyebrow mb-1 block">Value</label>
              <input
                type="number"
                className="input-shell w-full text-sm"
                placeholder="0"
                value={newValue}
                onChange={(e) => setNewValue(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") handleCreate();
                }}
              />
            </div>
            <button
              className="button-primary text-sm px-4 py-2"
              onClick={handleCreate}
              disabled={saving || !newName.trim()}
            >
              Add
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  HTML Question Text — renders HTML, click to edit raw source       */
/* ------------------------------------------------------------------ */

function HtmlQuestionText({
  html,
  onSave,
  placeholder = "Enter question text",
}: {
  html: string;
  onSave: (val: string) => void;
  placeholder?: string;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(html);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => { setDraft(html); }, [html]);
  useEffect(() => {
    if (editing && textareaRef.current) {
      textareaRef.current.focus();
      textareaRef.current.select();
    }
  }, [editing]);

  const commit = () => {
    setEditing(false);
    const trimmed = draft.trim();
    if (trimmed && trimmed !== html) onSave(trimmed);
    else setDraft(html);
  };

  const sanitized = useMemo(() => DOMPurify.sanitize(html || ""), [html]);

  if (editing) {
    return (
      <div className="space-y-1">
        <textarea
          ref={textareaRef}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => {
            if (e.key === "Escape") { setDraft(html); setEditing(false); }
          }}
          className="textarea-shell w-full text-sm font-mono"
          rows={4}
        />
        <div className="text-[10px] text-slate-400">HTML is supported. Press Escape to cancel.</div>
      </div>
    );
  }

  if (!html) {
    return (
      <span
        className="text-sm text-slate-400 italic cursor-pointer hover:bg-slate-100 rounded px-1 -mx-1"
        onClick={() => setEditing(true)}
        title="Click to edit"
      >
        {placeholder}
      </span>
    );
  }

  return (
    <div
      className="text-sm font-medium cursor-pointer hover:bg-slate-100 rounded px-1 -mx-1 transition-colors question-html-content"
      onClick={() => setEditing(true)}
      title="Click to edit"
      dangerouslySetInnerHTML={{ __html: sanitized }}
    />
  );
}

/* ------------------------------------------------------------------ */
/*  Choice Adder — dropdown to add point values to a question         */
/* ------------------------------------------------------------------ */

function ChoiceAdder({
  pointValues,
  selectedIds,
  onAdd,
  disabled,
}: {
  pointValues: PointValue[];
  selectedIds: Set<number>;
  onAdd: (pvId: number) => void;
  disabled: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState("");
  const btnRef = useRef<HTMLButtonElement>(null);
  const dropRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);

  const available = pointValues.filter(
    (pv) => !selectedIds.has(pv.id) && pv.name.toLowerCase().includes(filter.toLowerCase())
  );

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (
        btnRef.current && !btnRef.current.contains(e.target as Node) &&
        dropRef.current && !dropRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
        setFilter("");
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const handleOpen = () => {
    if (open) { setOpen(false); setFilter(""); return; }
    if (btnRef.current) {
      const rect = btnRef.current.getBoundingClientRect();
      setPos({ top: rect.bottom + 4, left: rect.left });
    }
    setOpen(true);
  };

  if (pointValues.length === 0) return null;
  const allAssigned = available.length === 0 && !filter;

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        className="inline-flex items-center gap-1 rounded-full border border-dashed border-slate-300 px-2.5 py-1 text-xs text-slate-500 hover:border-slate-400 hover:text-slate-700 transition-colors disabled:opacity-40"
        onClick={handleOpen}
        disabled={disabled || allAssigned}
        title={allAssigned ? "All point values assigned" : "Add answer choice"}
      >
        <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
        </svg>
        Add
      </button>
      {open && pos && ReactDOM.createPortal(
        <div
          ref={dropRef}
          className="fixed z-[9999] w-64 rounded-lg border border-slate-200 bg-white shadow-xl overflow-hidden rise-in"
          style={{ top: pos.top, left: pos.left }}
        >
          <div className="p-2 border-b border-slate-100">
            <input
              type="text"
              className="input-shell w-full text-xs"
              placeholder="Filter point values..."
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              autoFocus
            />
          </div>
          <div className="max-h-56 overflow-y-auto">
            {available.length === 0 ? (
              <div className="px-3 py-4 text-xs text-slate-400 text-center italic">
                {filter ? "No matches" : "All values assigned"}
              </div>
            ) : (
              available.map((pv) => (
                <button
                  key={pv.id}
                  type="button"
                  className="flex w-full items-start gap-2 px-3 py-2 text-xs hover:bg-slate-50 transition-colors text-left"
                  onClick={() => {
                    onAdd(pv.id);
                    setFilter("");
                  }}
                >
                  <div className="flex-1 min-w-0">
                    <div className="font-medium">{pv.name}</div>
                    {pv.used_in_surveys && pv.used_in_surveys.length > 0 && (
                      <div className="text-[10px] text-slate-400 mt-0.5 truncate" title={pv.used_in_surveys.join(", ")}>
                        Used in: {pv.used_in_surveys.join(", ")}
                      </div>
                    )}
                  </div>
                  {pv.value != null && (
                    <span
                      className={`font-mono text-[10px] px-1.5 py-0.5 rounded shrink-0 ${
                        pv.value > 0
                          ? "bg-emerald-50 text-emerald-600"
                          : pv.value < 0
                          ? "bg-red-50 text-red-600"
                          : "bg-slate-100 text-slate-500"
                      }`}
                    >
                      {pv.value > 0 ? "+" : ""}{pv.value}
                    </span>
                  )}
                </button>
              ))
            )}
          </div>
        </div>,
        document.body,
      )}
    </>
  );
}

/* ------------------------------------------------------------------ */
/*  Question Row                                                      */
/* ------------------------------------------------------------------ */

function QuestionRow({
  question,
  pointValues,
  isFirst,
  isLast,
  onReorder,
  onRefreshSurvey,
  showToast,
}: {
  question: QuestionWithChoices;
  pointValues: PointValue[];
  isFirst: boolean;
  isLast: boolean;
  onReorder: (direction: "up" | "down") => void;
  onRefreshSurvey: () => void;
  showToast: (msg: string, type: "success" | "error") => void;
}) {
  const [saving, setSaving] = useState(false);
  const selectedPvIds = new Set(question.choices.map((c) => c.point_value_id));

  const handleTextSave = async (text: string) => {
    try {
      await surveysApi.updateQuestion(question.id, { text });
      onRefreshSurvey();
      showToast("Question updated", "success");
    } catch (err) {
      showToast(extractError(err), "error");
    }
  };

  const handleMaxScoreSave = async (val: string) => {
    const num = parseFloat(val);
    if (isNaN(num) || num < 0) {
      showToast("Max score must be a non-negative number", "error");
      return;
    }
    try {
      await surveysApi.updateQuestion(question.id, { max_score: num });
      onRefreshSurvey();
      showToast("Max score updated", "success");
    } catch (err) {
      showToast(extractError(err), "error");
    }
  };

  const handleCommentPromptSave = async (commentPrompt: string) => {
    try {
      await surveysApi.updateQuestion(question.id, { comment_prompt: commentPrompt });
      onRefreshSurvey();
      showToast("Question comment helper updated", "success");
    } catch (err) {
      showToast(extractError(err), "error");
    }
  };

  const handleToggleChoice = async (pvId: number) => {
    setSaving(true);
    const currentIds = question.choices.map((c) => c.point_value_id);
    let nextIds: number[];
    if (currentIds.includes(pvId)) {
      nextIds = currentIds.filter((id) => id !== pvId);
    } else {
      nextIds = [...currentIds, pvId];
    }
    try {
      await surveysApi.updateQuestion(question.id, { choice_point_value_ids: nextIds });
      onRefreshSurvey();
    } catch (err) {
      showToast(extractError(err), "error");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm("Delete this question?")) return;
    try {
      await surveysApi.deleteQuestion(question.id);
      onRefreshSurvey();
      showToast("Question deleted", "success");
    } catch (err) {
      showToast(extractError(err), "error");
    }
  };

  return (
    <div className="surface-muted rounded-lg p-4 space-y-3">
      {/* Question header row */}
      <div className="flex items-start gap-3">
        {/* Order arrows */}
        <div className="flex flex-col gap-0.5 pt-0.5">
          <button
            className="button-ghost p-0.5 rounded disabled:opacity-30"
            onClick={() => onReorder("up")}
            disabled={isFirst}
            title="Move up"
          >
            <ArrowUpIcon />
          </button>
          <button
            className="button-ghost p-0.5 rounded disabled:opacity-30"
            onClick={() => onReorder("down")}
            disabled={isLast}
            title="Move down"
          >
            <ArrowDownIcon />
          </button>
        </div>

        {/* Order number */}
        <span className="text-xs text-slate-400 font-mono pt-1 min-w-[24px]">
          Q{question.order}
        </span>

        {/* Question text — render HTML, click to edit raw */}
        <div className="flex-1 min-w-0">
          <HtmlQuestionText
            html={question.text}
            onSave={handleTextSave}
            placeholder="Enter question text"
          />
        </div>

        {/* Max score */}
        <div className="flex items-center gap-1 flex-shrink-0">
          <span className="eyebrow text-[10px]">Max</span>
          <input
            type="number"
            className="input-shell w-16 text-xs text-center"
            value={question.max_score}
            onChange={() => {}}
            onBlur={(e) => {
              if (e.target.value !== String(question.max_score)) {
                handleMaxScoreSave(e.target.value);
              }
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                (e.target as HTMLInputElement).blur();
              }
            }}
            defaultValue={question.max_score}
            key={`${question.id}-${question.max_score}`}
            step="any"
            min="0"
          />
        </div>

        {/* Delete */}
        <button
          className="button-danger text-xs px-2 py-1 flex-shrink-0"
          onClick={handleDelete}
          title="Delete question"
        >
          Delete
        </button>
      </div>

      {/* Answer choices — only show assigned choices, with add button */}
      <div className="pl-12">
        <span className="eyebrow text-[10px] block mb-1.5">Suggested Comment Helper</span>
        <div className="rounded-lg border border-dashed border-slate-300 bg-white/70 px-3 py-2">
          <InlineEdit
            value={question.comment_prompt || ""}
            onSave={handleCommentPromptSave}
            placeholder="Add the reusable comment text shoppers can insert under this question"
            multiline
            tag="p"
            className="block text-sm leading-6 text-slate-600"
          />
        </div>
      </div>

      <div className="pl-12">
        <span className="eyebrow text-[10px] block mb-1.5">Answer Choices</span>
        <div className="flex flex-wrap items-center gap-1.5">
          {question.choices.length === 0 && (
            <span className="text-xs text-slate-400 italic mr-1">No choices assigned.</span>
          )}
          {question.choices
            .sort((a, b) => {
              const av = a.point_value_value ?? 0;
              const bv = b.point_value_value ?? 0;
              return bv - av;
            })
            .map((c) => (
              <span
                key={c.id}
                className={`inline-flex items-center gap-1 rounded-full pl-2.5 pr-1 py-1 text-xs font-medium border ${
                  c.point_value_value != null && c.point_value_value > 0
                    ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                    : c.point_value_value != null && c.point_value_value < 0
                    ? "bg-red-50 text-red-700 border-red-200"
                    : "bg-slate-50 text-slate-600 border-slate-200"
                }`}
              >
                {c.point_value_name}
                {c.point_value_value != null && (
                  <span className="font-mono text-[10px] opacity-70 ml-0.5">
                    {c.point_value_value > 0 ? "+" : ""}{c.point_value_value}
                  </span>
                )}
                <button
                  type="button"
                  className="ml-0.5 rounded-full hover:bg-black/10 p-0.5 transition-colors"
                  title={`Remove ${c.point_value_name}`}
                  onClick={() => handleToggleChoice(c.point_value_id)}
                  disabled={saving}
                >
                  <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </span>
            ))}
          {/* Add choice dropdown */}
          <ChoiceAdder
            pointValues={pointValues}
            selectedIds={selectedPvIds}
            onAdd={handleToggleChoice}
            disabled={saving}
          />
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Category Accordion                                                */
/* ------------------------------------------------------------------ */

function CategoryAccordion({
  category,
  pointValues,
  isFirst,
  isLast,
  onReorder,
  onRefreshSurvey,
  showToast,
}: {
  category: CategoryWithChoices;
  pointValues: PointValue[];
  isFirst: boolean;
  isLast: boolean;
  onReorder: (direction: "up" | "down") => void;
  onRefreshSurvey: () => void;
  showToast: (msg: string, type: "success" | "error") => void;
}) {
  const [open, setOpen] = useState(false);
  const [addingQuestion, setAddingQuestion] = useState(false);
  const [newQuestionText, setNewQuestionText] = useState("");
  const [newCommentPrompt, setNewCommentPrompt] = useState("");
  const [newMaxScore, setNewMaxScore] = useState("4");
  const [saving, setSaving] = useState(false);

  const handleNameSave = async (name: string) => {
    try {
      await surveysApi.updateCategory(category.id, { name });
      onRefreshSurvey();
      showToast("Category name updated", "success");
    } catch (err) {
      showToast(extractError(err), "error");
    }
  };

  const handleDelete = async () => {
    if (
      !window.confirm(
        `Delete category "${category.name}" and all its questions?`
      )
    )
      return;
    try {
      await surveysApi.deleteCategory(category.id);
      onRefreshSurvey();
      showToast("Category deleted", "success");
    } catch (err) {
      showToast(extractError(err), "error");
    }
  };

  const handleAddQuestion = async () => {
    if (!newQuestionText.trim()) return;
    setSaving(true);
    try {
      const maxScore = parseFloat(newMaxScore) || 4;
      const nextOrder = Math.max(0, ...category.questions.map((question) => question.order || 0)) + 1;
      const normalizedPointValues = pointValues.map((pointValue) => ({
        ...pointValue,
        normalizedName: pointValue.name.trim().toLowerCase().replace(/[\s_.\-/]+/g, ""),
      }));
      const yesPointValue = normalizedPointValues.find(
        (pointValue) => pointValue.normalizedName.startsWith("yes") && pointValue.value === maxScore
      );
      const noPointValue = normalizedPointValues.find(
        (pointValue) => pointValue.normalizedName.startsWith("no") && pointValue.value === 0
      );
      const notApplicablePointValue = normalizedPointValues.find(
        (pointValue) =>
          ["na", "n/a", "notapplicable"].includes(pointValue.normalizedName) ||
          pointValue.normalizedName.startsWith("notapplicable")
      );
      const defaultChoiceIds = [yesPointValue, noPointValue, notApplicablePointValue]
        .filter((pointValue): pointValue is PointValue & { normalizedName: string } => Boolean(pointValue))
        .map((pointValue) => pointValue.id);
      await surveysApi.createQuestion({
        category_id: category.id,
        text: newQuestionText.trim(),
        comment_prompt: newCommentPrompt.trim(),
        order: nextOrder,
        max_score: maxScore,
        choice_point_value_ids: defaultChoiceIds,
      });
      setNewQuestionText("");
      setNewCommentPrompt("");
      setNewMaxScore("4");
      setAddingQuestion(false);
      onRefreshSurvey();
      showToast("Question added", "success");
    } catch (err) {
      showToast(extractError(err), "error");
    } finally {
      setSaving(false);
    }
  };

  const handleQuestionReorder = async (
    questionId: number,
    currentOrder: number,
    direction: "up" | "down"
  ) => {
    const newOrder = direction === "up" ? currentOrder - 1 : currentOrder + 1;
    if (newOrder < 1) return;
    try {
      await surveysApi.updateQuestion(questionId, { order: newOrder });
      onRefreshSurvey();
    } catch (err) {
      showToast(extractError(err), "error");
    }
  };

  const questionCount = category.questions.length;

  return (
    <div className="border border-slate-200 rounded-xl rise-in">
      {/* Category header */}
      <div className="flex items-center gap-2 px-4 py-3 bg-slate-50 hover:bg-slate-100 transition-colors">
        {/* Ordering arrows */}
        <div className="flex gap-0.5">
          <button
            className="button-ghost p-0.5 rounded disabled:opacity-30"
            onClick={() => onReorder("up")}
            disabled={isFirst}
            title="Move up"
          >
            <ArrowUpIcon />
          </button>
          <button
            className="button-ghost p-0.5 rounded disabled:opacity-30"
            onClick={() => onReorder("down")}
            disabled={isLast}
            title="Move down"
          >
            <ArrowDownIcon />
          </button>
        </div>

        {/* Expand/Collapse */}
        <button
          type="button"
          onClick={() => setOpen(!open)}
          className="flex items-center gap-2 flex-1 min-w-0"
        >
          <ChevronIcon open={open} />
          <span className="label-caps truncate">{category.name}</span>
          <span className="badge-pill text-xs flex-shrink-0">
            {questionCount} {questionCount === 1 ? "question" : "questions"}
          </span>
        </button>

        {/* Category inline edit + delete */}
        <div className="flex items-center gap-2 flex-shrink-0">
          <button
            className="button-ghost text-xs px-2 py-1"
            onClick={(e) => {
              e.stopPropagation();
              const newName = window.prompt("Rename category:", category.name);
              if (newName && newName.trim() !== category.name) {
                handleNameSave(newName.trim());
              }
            }}
          >
            Rename
          </button>
          <button
            className="button-danger text-xs px-2 py-1"
            onClick={(e) => {
              e.stopPropagation();
              handleDelete();
            }}
          >
            Delete
          </button>
        </div>
      </div>

      {/* Category body */}
      {open && (
        <div className="p-4 space-y-3">
          {category.questions.length === 0 ? (
            <div className="text-center py-8 text-sm text-slate-400 italic">
              No questions yet. Add the first question below.
            </div>
          ) : (
            category.questions
              .sort((a, b) => a.order - b.order)
              .map((q, idx) => (
                <QuestionRow
                  key={q.id}
                  question={q}
                  pointValues={pointValues}
                  isFirst={idx === 0}
                  isLast={idx === category.questions.length - 1}
                  onReorder={(dir) =>
                    handleQuestionReorder(q.id, q.order, dir)
                  }
                  onRefreshSurvey={onRefreshSurvey}
                  showToast={showToast}
                />
              ))
          )}

          {/* Add question form */}
          {addingQuestion ? (
            <div className="border border-dashed border-slate-300 rounded-lg p-4 space-y-3 rise-in">
              <div className="flex gap-3">
                <div className="flex-1">
                  <label className="eyebrow text-[10px] block mb-1">Question Text</label>
                  <input
                    type="text"
                    className="input-shell w-full text-sm"
                    placeholder="Enter question text"
                    value={newQuestionText}
                    onChange={(e) => setNewQuestionText(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") handleAddQuestion();
                      if (e.key === "Escape") setAddingQuestion(false);
                    }}
                    autoFocus
                  />
                </div>
                <div className="w-24">
                  <label className="eyebrow text-[10px] block mb-1">Max Score</label>
                  <input
                    type="number"
                    className="input-shell w-full text-sm"
                    value={newMaxScore}
                    onChange={(e) => setNewMaxScore(e.target.value)}
                    step="any"
                    min="0"
                  />
                </div>
              </div>
              <div>
                <label className="eyebrow text-[10px] block mb-1">Suggested Comment Helper</label>
                <textarea
                  className="textarea-shell w-full text-sm"
                  placeholder="Reusable comment text shoppers can insert for this question"
                  value={newCommentPrompt}
                  onChange={(e) => setNewCommentPrompt(e.target.value)}
                  rows={3}
                />
              </div>
              <div className="flex gap-2">
                <button
                  className="button-primary text-xs px-4 py-1.5"
                  onClick={handleAddQuestion}
                  disabled={saving || !newQuestionText.trim()}
                >
                  {saving ? "Saving..." : "Add Question"}
                </button>
                <button
                  className="button-ghost text-xs px-4 py-1.5"
                  onClick={() => {
                    setAddingQuestion(false);
                    setNewQuestionText("");
                    setNewCommentPrompt("");
                    setNewMaxScore("4");
                  }}
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <button
              className="button-ghost text-xs px-4 py-2 w-full border border-dashed border-slate-300 rounded-lg hover:border-slate-400 transition-colors"
              onClick={() => setAddingQuestion(true)}
            >
              + Add Question
            </button>
          )}
        </div>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Survey Editor (right panel)                                       */
/* ------------------------------------------------------------------ */

function SurveyEditor({
  surveyId,
  pointValues,
  onRefreshList,
  showToast,
}: {
  surveyId: number;
  pointValues: PointValue[];
  onRefreshList: () => void;
  showToast: (msg: string, type: "success" | "error") => void;
}) {
  const [survey, setSurvey] = useState<Survey | null>(null);
  const [categories, setCategories] = useState<CategoryWithChoices[]>([]);
  const [loading, setLoading] = useState(true);
  const [addingCategory, setAddingCategory] = useState(false);
  const [newCatName, setNewCatName] = useState("");
  const [savingCat, setSavingCat] = useState(false);
  // HMC assignment
  const [hmcAssignments, setHmcAssignments] = useState<{ contract_spec_id: number; management_company_id: number; management_company_name: string }[]>([]);
  const [allMCs, setAllMCs] = useState<{ id: number; name: string }[]>([]);
  const [assigningHMC, setAssigningHMC] = useState(false);
  const [selectedMCId, setSelectedMCId] = useState<number | null>(null);
  const [hmcOpen, setHmcOpen] = useState(false);

  const loadHMCs = useCallback(async () => {
    try {
      const [assigned, mcs] = await Promise.all([
        surveysApi.templateHMCs(surveyId),
        hotelsApi.managementCompanies.list("limit=1000"),
      ]);
      setHmcAssignments(assigned);
      const mcList = Array.isArray(mcs) ? mcs : Array.isArray((mcs as any).items) ? (mcs as any).items : [];
      setAllMCs(mcList);
    } catch {}
  }, [surveyId]);

  useEffect(() => { loadHMCs(); }, [loadHMCs]);

  const loadSurvey = useCallback(async () => {
    try {
      const data = await surveysApi.getTemplate(surveyId);
      setSurvey(data);

      // Load questions with choices for each category
      const catsWithChoices: CategoryWithChoices[] = [];
      for (const cat of data.categories) {
        try {
          const questionsWithChoices = await surveysApi.questionsByCategory(cat.id);
          catsWithChoices.push({
            ...cat,
            questions: (questionsWithChoices || []).map((q: any) => ({
              id: q.id,
              text: q.text,
              comment_prompt: q.comment_prompt || "",
              order: q.order,
              max_score: q.max_score,
              choices: (q.choices || []).map((c: any) => ({
                id: c.id,
                point_value_id: c.point_value_id,
                point_value_name: c.point_value_name || "",
                point_value_value: c.point_value_value ?? c.point_value_score ?? null,
              })),
            })),
          });
        } catch {
          catsWithChoices.push({ ...cat, questions: [] });
        }
      }
      setCategories(catsWithChoices.sort((a, b) => a.order - b.order));
    } catch (err) {
      showToast(extractError(err), "error");
    } finally {
      setLoading(false);
    }
  }, [surveyId, showToast]);

  useEffect(() => {
    setLoading(true);
    loadSurvey();
  }, [loadSurvey]);

  const handleNameSave = async (name: string) => {
    try {
      await surveysApi.updateTemplate(surveyId, { name });
      loadSurvey();
      onRefreshList();
      showToast("Survey name updated", "success");
    } catch (err) {
      showToast(extractError(err), "error");
    }
  };

  const handleDescSave = async (description: string) => {
    try {
      await surveysApi.updateTemplate(surveyId, { description });
      loadSurvey();
      showToast("Description updated", "success");
    } catch (err) {
      showToast(extractError(err), "error");
    }
  };

  const handleToggleActive = async () => {
    if (!survey) return;
    const nextActive = !(survey as any).is_active;
    try {
      await surveysApi.updateTemplate(surveyId, { is_active: nextActive });
      loadSurvey();
      onRefreshList();
      showToast(nextActive ? "Survey activated" : "Survey deactivated", "success");
    } catch (err) {
      showToast(extractError(err), "error");
    }
  };

  const handleAddCategory = async () => {
    if (!newCatName.trim()) return;
    setSavingCat(true);
    try {
      await surveysApi.createCategory({
        survey_id: surveyId,
        name: newCatName.trim(),
      });
      setNewCatName("");
      setAddingCategory(false);
      loadSurvey();
      onRefreshList();
      showToast("Category added", "success");
    } catch (err) {
      showToast(extractError(err), "error");
    } finally {
      setSavingCat(false);
    }
  };

  const handleCategoryReorder = async (
    catId: number,
    currentOrder: number,
    direction: "up" | "down"
  ) => {
    const newOrder = direction === "up" ? currentOrder - 1 : currentOrder + 1;
    if (newOrder < 1) return;
    try {
      await surveysApi.updateCategory(catId, { order: newOrder });
      loadSurvey();
    } catch (err) {
      showToast(extractError(err), "error");
    }
  };

  const handleDeleteSurvey = async () => {
    if (!window.confirm("Delete this survey template? This will soft-delete it.")) return;
    try {
      await surveysApi.deleteTemplate(surveyId);
      onRefreshList();
      showToast("Survey deleted", "success");
    } catch (err) {
      showToast(extractError(err), "error");
    }
  };

  const handleClone = async () => {
    const name = window.prompt("Name for the cloned survey:", `${survey?.name || "Survey"} (Copy)`);
    if (!name) return;
    try {
      const cloned = await surveysApi.cloneTemplate(surveyId, name);
      onRefreshList();
      showToast(`Survey cloned as "${cloned.name}"`, "success");
    } catch (err) {
      showToast(extractError(err), "error");
    }
  };

  const handleAssignHMC = async () => {
    if (!selectedMCId) return;
    setAssigningHMC(true);
    try {
      await surveysApi.assignHMC(surveyId, selectedMCId);
      await loadHMCs();
      setSelectedMCId(null);
      showToast("Survey assigned to HMC", "success");
    } catch (err) {
      showToast(extractError(err), "error");
    } finally {
      setAssigningHMC(false);
    }
  };

  const handleUnassignHMC = async (mcId: number, mcName: string) => {
    if (!window.confirm(`Remove this survey from ${mcName}?`)) return;
    try {
      await surveysApi.unassignHMC(surveyId, mcId);
      await loadHMCs();
      showToast(`Survey unassigned from ${mcName}`, "success");
    } catch (err) {
      showToast(extractError(err), "error");
    }
  };

  if (loading) {
    return (
      <div className="surface-panel rounded-xl p-10 text-center">
        <div className="animate-spin inline-block h-8 w-8 border-2 border-blue-600 border-t-transparent rounded-full" />
        <p className="text-sm text-slate-500 mt-3">Loading survey...</p>
      </div>
    );
  }

  if (!survey) {
    return (
      <div className="surface-panel rounded-xl p-10 text-center text-sm text-slate-500">
        Survey not found.
      </div>
    );
  }

  // Stats
  const totalCategories = categories.length;
  const totalQuestions = categories.reduce((sum, c) => sum + c.questions.length, 0);
  const totalMaxScore = categories.reduce(
    (sum, c) => sum + c.questions.reduce((qs, q) => qs + (q.max_score || 0), 0),
    0
  );

  // Point values actually used in this survey
  const usedPvIds = new Set<number>();
  for (const cat of categories) {
    for (const q of cat.questions) {
      for (const c of q.choices) usedPvIds.add(c.point_value_id);
    }
  }
  const usedPointValues = pointValues.filter((pv) => usedPvIds.has(pv.id));

  // Available MCs for assignment (exclude already-assigned ones)
  const assignedMCIds = new Set(hmcAssignments.map((a) => a.management_company_id));
  const availableMCs = allMCs.filter((mc) => !assignedMCIds.has(mc.id));

  return (
    <div className="space-y-5 rise-in">
      {/* Survey header */}
      <div className="surface-hero rounded-xl p-6 space-y-4">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1 min-w-0">
            <InlineEdit
              value={survey.name}
              onSave={handleNameSave}
              tag="h2"
              className="text-xl font-bold"
              placeholder="Untitled Survey"
            />
            <InlineEdit
              value={survey.description || ""}
              onSave={handleDescSave}
              tag="p"
              className="text-sm text-slate-500 mt-1"
              placeholder="Add a description..."
              multiline
            />
          </div>

          <div className="flex flex-wrap items-center gap-2 flex-shrink-0">
            {/* Active toggle */}
            <button
              onClick={handleToggleActive}
              className={`inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-xs font-medium transition-colors ${
                (survey as any).is_active !== false
                  ? "bg-emerald-100 text-emerald-700 hover:bg-emerald-200"
                  : "bg-slate-100 text-slate-500 hover:bg-slate-200"
              }`}
            >
              <span
                className={`inline-block h-2 w-2 rounded-full ${
                  (survey as any).is_active !== false ? "bg-emerald-500" : "bg-slate-400"
                }`}
              />
              {(survey as any).is_active !== false ? "Active" : "Inactive"}
            </button>

            <button
              className="button-ghost text-xs px-3 py-1.5"
              onClick={handleClone}
              title="Duplicate this survey (deep copy)"
            >
              Duplicate
            </button>

            <button
              className="button-danger text-xs px-3 py-1.5"
              onClick={handleDeleteSurvey}
            >
              Delete
            </button>
          </div>
        </div>

        {/* Stats bar */}
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          <div className="kpi-card text-center px-4 py-3 rounded-lg">
            <div className="text-lg font-bold">{totalCategories}</div>
            <div className="eyebrow text-[10px]">Categories</div>
          </div>
          <div className="kpi-card text-center px-4 py-3 rounded-lg">
            <div className="text-lg font-bold">{totalQuestions}</div>
            <div className="eyebrow text-[10px]">Questions</div>
          </div>
          <div className="kpi-card text-center px-4 py-3 rounded-lg">
            <div className="text-lg font-bold">{totalMaxScore}</div>
            <div className="eyebrow text-[10px]">Max Score</div>
          </div>
          <div className="kpi-card text-center px-4 py-3 rounded-lg">
            <div className="text-lg font-bold">{usedPointValues.length}</div>
            <div className="eyebrow text-[10px]">Point Values</div>
          </div>
        </div>

        {/* Point values used in this survey */}
        {usedPointValues.length > 0 && (
          <div>
            <span className="eyebrow text-[10px] block mb-1.5">Scoring Choices in This Survey</span>
            <div className="flex flex-wrap gap-1.5">
              {usedPointValues
                .sort((a, b) => (b.value ?? 0) - (a.value ?? 0))
                .map((pv) => (
                  <span
                    key={pv.id}
                    className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-medium ${
                      pv.value != null && pv.value > 0
                        ? "bg-emerald-50 text-emerald-700"
                        : pv.value != null && pv.value < 0
                        ? "bg-red-50 text-red-700"
                        : "bg-slate-100 text-slate-600"
                    }`}
                  >
                    {pv.name}
                    {pv.value != null && (
                      <span className="font-mono text-[10px] opacity-70">
                        {pv.value > 0 ? "+" : ""}{pv.value}
                      </span>
                    )}
                  </span>
                ))}
            </div>
          </div>
        )}
      </div>

      {/* HMC Assignments */}
      <div className="surface-panel rounded-xl border border-slate-200 overflow-hidden">
        <button
          type="button"
          onClick={() => setHmcOpen(!hmcOpen)}
          className="flex w-full items-center justify-between px-5 py-3 bg-slate-50 hover:bg-slate-100 transition-colors"
        >
          <span className="label-caps flex items-center gap-2">
            Assigned Management Companies
            <span className="badge-pill text-xs">{hmcAssignments.length}</span>
          </span>
          <ChevronIcon open={hmcOpen} />
        </button>

        {hmcOpen && (
          <div className="p-5 space-y-3 rise-in">
            {hmcAssignments.length === 0 ? (
              <p className="text-sm text-slate-400 italic">
                Not assigned to any HMC. Assign this survey to an HMC's contract spec below.
              </p>
            ) : (
              <div className="flex flex-wrap gap-2">
                {hmcAssignments.map((a) => (
                  <span
                    key={a.management_company_id}
                    className="inline-flex items-center gap-1.5 rounded-full border border-blue-200 bg-blue-50 pl-3 pr-1.5 py-1.5 text-xs font-medium text-blue-700"
                  >
                    {a.management_company_name}
                    <button
                      type="button"
                      className="rounded-full hover:bg-blue-200 p-0.5 transition-colors"
                      title={`Unassign from ${a.management_company_name}`}
                      onClick={() => handleUnassignHMC(a.management_company_id, a.management_company_name)}
                    >
                      <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </span>
                ))}
              </div>
            )}

            {/* Assign to new HMC */}
            <div className="flex items-end gap-3 pt-2 border-t border-slate-100">
              <div className="flex-1">
                <label className="eyebrow text-[10px] block mb-1">Assign to HMC</label>
                <SearchableSelect
                  options={availableMCs.map((mc) => ({ value: mc.id, label: mc.name }))}
                  value={selectedMCId ?? ""}
                  onChange={(v) => setSelectedMCId(v ? Number(v) : null)}
                  placeholder="Select management company..."
                />
              </div>
              <button
                className="button-primary text-sm px-4 py-2"
                onClick={handleAssignHMC}
                disabled={!selectedMCId || assigningHMC}
              >
                {assigningHMC ? "Assigning..." : "Assign"}
              </button>
            </div>
            <p className="text-[11px] text-slate-400">
              Assigning updates the HMC's contract spec to use this survey template. Duplicate the survey first if you need a different version for each HMC.
            </p>
          </div>
        )}
      </div>

      {/* Categories */}
      <div className="space-y-4">
        {categories.length === 0 && (
          <div className="surface-panel rounded-xl p-10 text-center text-sm text-slate-400 italic">
            No categories yet. Add the first category to start building this survey.
          </div>
        )}

        {categories.map((cat, idx) => (
          <CategoryAccordion
            key={cat.id}
            category={cat}
            pointValues={pointValues}
            isFirst={idx === 0}
            isLast={idx === categories.length - 1}
            onReorder={(dir) => handleCategoryReorder(cat.id, cat.order, dir)}
            onRefreshSurvey={loadSurvey}
            showToast={showToast}
          />
        ))}
      </div>

      {/* Add category */}
      {addingCategory ? (
        <div className="border border-dashed border-slate-300 rounded-xl p-4 space-y-3 rise-in">
          <label className="eyebrow text-[10px] block">New Category Name</label>
          <div className="flex gap-3">
            <input
              type="text"
              className="input-shell flex-1 text-sm"
              placeholder="e.g. Administration, Hospitality, Qualifying..."
              value={newCatName}
              onChange={(e) => setNewCatName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") handleAddCategory();
                if (e.key === "Escape") {
                  setAddingCategory(false);
                  setNewCatName("");
                }
              }}
              autoFocus
            />
            <button
              className="button-primary text-sm px-4 py-2"
              onClick={handleAddCategory}
              disabled={savingCat || !newCatName.trim()}
            >
              {savingCat ? "Saving..." : "Add Category"}
            </button>
            <button
              className="button-ghost text-sm px-4 py-2"
              onClick={() => {
                setAddingCategory(false);
                setNewCatName("");
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <button
          className="button-accent text-sm px-5 py-2.5 w-full rounded-xl border border-dashed border-slate-300 hover:border-slate-400 transition-colors"
          onClick={() => setAddingCategory(true)}
        >
          + Add Category
        </button>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Survey List Item Card                                             */
/* ------------------------------------------------------------------ */

function SurveyCard({
  item,
  isSelected,
  onClick,
}: {
  item: SurveyListItem;
  isSelected: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`w-full text-left rounded-lg p-3 transition-colors border ${
        isSelected
          ? "border-blue-300 bg-blue-50 shadow-sm"
          : "border-transparent hover:bg-slate-50"
      }`}
    >
      <div className="flex items-center justify-between gap-2">
        <span className="text-sm font-medium truncate">{item.name}</span>
        <span
          className={`inline-block h-2 w-2 rounded-full flex-shrink-0 ${
            item.is_active ? "bg-emerald-500" : "bg-slate-300"
          }`}
          title={item.is_active ? "Active" : "Inactive"}
        />
      </div>
    </button>
  );
}

/* ------------------------------------------------------------------ */
/*  Main Page                                                         */
/* ------------------------------------------------------------------ */

export default function SurveyBuilderPage() {
  /* --- State --- */
  const [surveyList, setSurveyList] = useState<SurveyListItem[]>([]);
  const [pointValues, setPointValues] = useState<PointValue[]>([]);
  const [search, setSearch] = useState("");
  const [showInactive, setShowInactive] = useState(false);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState("");
  const [newDesc, setNewDesc] = useState("");
  const [savingNew, setSavingNew] = useState(false);
  const [listLoading, setListLoading] = useState(true);
  const [toast, setToast] = useState<{ message: string; type: "success" | "error" } | null>(null);

  const showToast = useCallback((message: string, type: "success" | "error") => {
    setToast({ message, type });
  }, []);

  /* --- Load survey list --- */
  const loadList = useCallback(async () => {
    try {
      const data = await surveysApi.templates(showInactive);
      setSurveyList(data);
    } catch (err) {
      showToast(extractError(err), "error");
    } finally {
      setListLoading(false);
    }
  }, [showInactive, showToast]);

  const loadPointValues = useCallback(async () => {
    try {
      const data = await surveysApi.pointValues();
      setPointValues(data);
    } catch (err) {
      showToast(extractError(err), "error");
    }
  }, [showToast]);

  useEffect(() => {
    loadList();
  }, [loadList]);

  useEffect(() => {
    loadPointValues();
  }, [loadPointValues]);

  /* --- Filtered list --- */
  const filtered = surveyList.filter((s) =>
    s.name.toLowerCase().includes(search.toLowerCase())
  );

  /* --- Create survey --- */
  const handleCreate = async () => {
    if (!newName.trim()) return;
    setSavingNew(true);
    try {
      const created = await surveysApi.createTemplate({
        name: newName.trim(),
        description: newDesc.trim() || undefined,
      });
      setNewName("");
      setNewDesc("");
      setCreating(false);
      await loadList();
      setSelectedId(created.id);
      showToast("Survey created", "success");
    } catch (err) {
      showToast(extractError(err), "error");
    } finally {
      setSavingNew(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Survey Builder</h1>
          <p className="text-sm text-slate-500 mt-1">
            Create and manage survey templates, categories, questions, and scoring
          </p>
        </div>
      </div>

      {/* Library toolbar and survey picker stay above the editor so the canvas keeps full width. */}
      <section className="surface-panel rounded-xl border border-slate-200 overflow-hidden">
        <div className="p-4 space-y-4 border-b border-slate-100">
          <div className="flex flex-col gap-3 xl:flex-row xl:items-center xl:justify-between">
            <div className="flex flex-wrap items-center gap-2">
              <div className="badge-pill bg-slate-100 text-slate-700">
                {filtered.length} visible survey{filtered.length !== 1 ? "s" : ""}
              </div>
              {selectedId && (
                <div className="badge-pill bg-blue-50 text-blue-700">
                  Editing #{selectedId}
                </div>
              )}
            </div>

            <div className="grid gap-3 xl:grid-cols-[minmax(16rem,24rem),auto,auto] xl:items-center">
              <input
                type="text"
                className="input-shell w-full text-sm"
                placeholder="Search surveys..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
              <label className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer whitespace-nowrap">
                <input
                  type="checkbox"
                  checked={showInactive}
                  onChange={(e) => setShowInactive(e.target.checked)}
                  className="rounded border-slate-300"
                />
                Show inactive
              </label>
              <button
                className="button-primary text-sm px-4 py-2"
                onClick={() => setCreating(true)}
              >
                + New Survey
              </button>
            </div>
          </div>

          {creating && (
            <div className="rounded-2xl border border-slate-200 bg-blue-50/50 p-4 space-y-3 rise-in">
              <div className="grid gap-3 xl:grid-cols-[minmax(0,1fr),minmax(0,1fr),auto] xl:items-end">
                <div>
                  <label className="eyebrow text-[10px] block mb-1">Name</label>
                  <input
                    type="text"
                    className="input-shell w-full text-sm"
                    placeholder="Survey name"
                    value={newName}
                    onChange={(e) => setNewName(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") handleCreate();
                      if (e.key === "Escape") {
                        setCreating(false);
                        setNewName("");
                        setNewDesc("");
                      }
                    }}
                    autoFocus
                  />
                </div>
                <div>
                  <label className="eyebrow text-[10px] block mb-1">Description</label>
                  <textarea
                    className="textarea-shell w-full text-sm"
                    placeholder="Optional description"
                    rows={2}
                    value={newDesc}
                    onChange={(e) => setNewDesc(e.target.value)}
                  />
                </div>
                <div className="flex gap-2 xl:justify-end">
                  <button
                    className="button-primary text-sm px-4 py-2"
                    onClick={handleCreate}
                    disabled={savingNew || !newName.trim()}
                  >
                    {savingNew ? "Creating..." : "Create"}
                  </button>
                  <button
                    className="button-ghost text-sm px-4 py-2"
                    onClick={() => {
                      setCreating(false);
                      setNewName("");
                      setNewDesc("");
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          )}

          <div className="max-h-[16rem] overflow-y-auto">
            {listLoading ? (
              <div className="py-8 text-center">
                <div className="animate-spin inline-block h-6 w-6 border-2 border-blue-600 border-t-transparent rounded-full" />
              </div>
            ) : filtered.length === 0 ? (
              <div className="py-8 text-center text-sm text-slate-400">
                {search ? "No surveys match your search." : "No surveys found."}
              </div>
            ) : (
              <div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-4">
                {filtered.map((s) => (
                  <SurveyCard
                    key={s.id}
                    item={s}
                    isSelected={selectedId === s.id}
                    onClick={() => setSelectedId(s.id)}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Editor stays full width once a survey is selected. */}
      <div className="min-w-0">
        {selectedId ? (
          <SurveyEditor
            key={selectedId}
            surveyId={selectedId}
            pointValues={pointValues}
            onRefreshList={loadList}
            showToast={showToast}
          />
        ) : (
          <div className="surface-panel rounded-xl p-16 text-center rise-in delay-1">
            <svg
              className="mx-auto h-16 w-16 text-slate-300"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={1}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
              />
            </svg>
            <h3 className="mt-4 text-lg font-semibold text-slate-600">
              Select a survey to edit
            </h3>
            <p className="mt-2 text-sm text-slate-400">
              Pick a survey from the library above, or create a new one.
            </p>
          </div>
        )}
      </div>

      {/* ---- Bottom Panel: Point Values Manager ---- */}
      <PointValuesManager
        pointValues={pointValues}
        onRefresh={() => {
          loadPointValues();
          // Also refresh the survey editor if one is open, since choices may have changed
          if (selectedId) {
            setSelectedId((prev) => {
              // Force re-render by toggling; we use a key on SurveyEditor
              setTimeout(() => setSelectedId(prev), 0);
              return null;
            });
          }
        }}
        showToast={showToast}
      />

      {/* Toast */}
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
    </div>
  );
}
